import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteIcon from '@mui/icons-material/Delete';

function CustomersTable({ customers, onDelete }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [globalFilter, setGlobalFilter] = useState('');

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue, row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                row.original.stripe_customer_id ? (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                    }}
                  >
                    <CreditCardIcon sx={{ fontSize: 10, color: 'white' }} />
                  </Box>
                ) : null
              }
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.875rem',
                  bgcolor: 'primary.main',
                }}
              >
                {getInitials(getValue())}
              </Avatar>
            </Badge>
            <Box>
              <Link
                to={`/admin/customer/${row.original.id}`}
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  textDecoration: 'none',
                }}
              >
                {getValue()}
              </Link>
              {row.original.admin_notes && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                  📝 Has notes
                </Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => (
          <Typography variant="body2" color="text.secondary">
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue() || '-'}</Typography>
        ),
      },
      {
        accessorKey: 'booking_count',
        header: 'Bookings',
        cell: ({ getValue }) => {
          const count = getValue() || 0;
          return (
            <Chip
              label={count}
              size="small"
              color={count > 0 ? 'info' : 'default'}
              sx={{ fontWeight: 500, minWidth: 40 }}
            />
          );
        },
      },
      {
        accessorKey: 'total_spent',
        header: 'Total Spent',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            £{(getValue() || 0).toFixed(2)}
          </Typography>
        ),
      },
      {
        accessorKey: 'last_visit',
        header: 'Last Visit',
        cell: ({ getValue }) => (
          <Typography variant="body2" color="text.secondary">
            {formatDate(getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Customer Since',
        cell: ({ getValue }) => (
          <Typography variant="body2" color="text.secondary">
            {formatDate(getValue())}
          </Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              to={`/admin/customer/${row.original.id}`}
              variant="outlined"
              size="small"
              sx={{ minWidth: 60 }}
            >
              View
            </Button>
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onDelete(row.original)}
                sx={{ minWidth: 40 }}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            )}
          </Box>
        ),
      },
    ],
    [theme]
  );

  const table = useReactTable({
    data: customers,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  if (customers.length === 0 && !globalFilter) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          borderRadius: 2,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">No customers yet</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    const filteredData = table.getFilteredRowModel().rows;

    return (
      <Box>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, phone..."
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {filteredData.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No customers match your search</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredData.map((row) => {
              const customer = row.original;
              return (
                <Paper
                  key={customer.id}
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          customer.stripe_customer_id ? (
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid white',
                              }}
                            >
                              <CreditCardIcon sx={{ fontSize: 11, color: 'white' }} />
                            </Box>
                          ) : null
                        }
                      >
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            fontSize: '1rem',
                            bgcolor: 'primary.main',
                          }}
                        >
                          {getInitials(customer.name)}
                        </Avatar>
                      </Badge>
                      <Box>
                        <Link
                          to={`/admin/customer/${customer.id}`}
                          style={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            textDecoration: 'none',
                            fontSize: '1.125rem',
                          }}
                        >
                          {customer.name}
                        </Link>
                        {customer.admin_notes && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            📝 Has notes
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={`${customer.booking_count || 0} bookings`}
                      size="small"
                      color={customer.booking_count > 0 ? 'info' : 'default'}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {customer.email}
                    </Typography>
                    {customer.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.phone}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Spent
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        £{(customer.total_spent || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Visit
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(customer.last_visit)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
                    <Button
                      component={Link}
                      to={`/admin/customer/${customer.id}`}
                      variant="contained"
                      size="small"
                      sx={{ flex: 1 }}
                    >
                      View Details
                    </Button>
                    {onDelete && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => onDelete(customer)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop table view with sorting
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {table.getFilteredRowModel().rows.length} customers
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Search by name, email, phone..."
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {table.getFilteredRowModel().rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>No customers match your search</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 600,
                        bgcolor: 'grey.50',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() && (
                          header.column.getIsSorted() === 'asc' ? (
                            <ArrowUpwardIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" />
                          )
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} hover>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default CustomersTable;
